import {FormEvent, useEffect, useState} from "react";
import {Alert, Badge, Button, Card, Col, Form, Row, Table} from "react-bootstrap";
import {Link, useParams} from "react-router";
import {LicensesApi} from "../../api/LicensesApi";
import {UsersApi} from "../../api/UsersApi";
import {Editions, LicenseActivationViewModel, LicenseViewModel, UserViewModel} from "../../api/models";
import {useAsync} from "../../components/useAsync";
import {ErrorAlert} from "../../components/ErrorAlert";
import {LoadingSpinner} from "../../components/LoadingSpinner";
import {PageHeader} from "../../components/PageHeader";
import {ConfirmModal} from "../../components/ConfirmModal";
import {CopyButton} from "../../components/CopyButton";
import {SubmitButton} from "../../components/SubmitButton";
import {formatDateTime} from "../../components/format";
import {OwnerSelect} from "./LicensesPage";

export function LicenseDetailPage() {
    const {licenseId = ''} = useParams();
    const {data, error, loading, setData} = useAsync(
        () => Promise.all([LicensesApi.get(licenseId), UsersApi.getAll()]), [licenseId]);
    const [license, users] = data ?? [];
    const setLicense = (updated: LicenseViewModel) => setData(current => current && [updated, current[1]]);

    return (
        <>
            <div className="mb-2"><Link to="/admin/licenses" className="small"><i className="bi bi-arrow-left me-1"></i>All licences</Link></div>
            <ErrorAlert error={error} messages={{ 404: 'There is no such licence.' }}/>
            {loading && !data && <LoadingSpinner/>}
            {license && users && (
                <>
                    <PageHeader title="TSA-1 licence">
                        {license.revoked && <Badge bg="danger" className="fs-6">Revoked</Badge>}
                    </PageHeader>
                    <Row className="g-4">
                        <Col lg={5}><LicenseForm license={license} users={users} onSaved={setLicense}/></Col>
                        <Col lg={7}><Activations license={license} onChanged={setLicense}/></Col>
                    </Row>
                </>
            )}
        </>
    );
}

function LicenseForm({license, users, onSaved}: { license: LicenseViewModel, users: UserViewModel[], onSaved(license: LicenseViewModel): void }) {
    const [licensee, setLicensee] = useState(license.licensee);
    const [edition, setEdition] = useState(license.edition);
    const [owner, setOwner] = useState(license.ownerUsername);
    const [busy, setBusy] = useState(false);
    const [saved, setSaved] = useState(false);
    const [confirmRevoke, setConfirmRevoke] = useState(false);
    const [error, setError] = useState<unknown>();

    useEffect(() => {
        setLicensee(license.licensee);
        setEdition(license.edition);
        setOwner(license.ownerUsername);
    }, [license]);

    const save = async (revoked: boolean) => {
        setBusy(true);
        setSaved(false);
        setError(undefined);
        try {
            onSaved(await LicensesApi.update(license.id, { licensee: licensee.trim(), edition, ownerUsername: owner, revoked }));
            setSaved(true);
        } catch (err) {
            setError(err);
        } finally {
            setBusy(false);
            setConfirmRevoke(false);
        }
    };

    const submit = (e: FormEvent) => {
        e.preventDefault();
        void save(license.revoked);
    };

    return (
        <Card>
            <Card.Body>
                <div className="d-flex align-items-center gap-2 mb-3">
                    <span className="license-key fs-5">{license.key}</span>
                    <CopyButton text={license.key} label="Copy licence key"/>
                </div>
                <p className="text-body-secondary small">
                    Created {formatDateTime(license.createdOn)}. {license.activationsThisYear} of {license.activationsPerYear} new
                    activations used in the last 12 months.
                </p>
                <ErrorAlert error={error}/>
                {saved && <Alert variant="success" dismissible onClose={() => setSaved(false)}>Saved.</Alert>}
                <Form onSubmit={submit}>
                    <Form.Group className="mb-3" controlId="license-owner">
                        <Form.Label>Owner</Form.Label>
                        <OwnerSelect id="license-owner" users={users} value={owner} onChange={setOwner}/>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="license-licensee">
                        <Form.Label>Licensee</Form.Label>
                        <Form.Control required value={licensee} onChange={e => setLicensee(e.target.value)}/>
                        <Form.Text>Computers pick up a new name when they next renew.</Form.Text>
                    </Form.Group>
                    <Form.Group className="mb-4" controlId="license-edition">
                        <Form.Label>Edition</Form.Label>
                        <Form.Select value={edition} onChange={e => setEdition(e.target.value)}>
                            {[...new Set([...Editions, license.edition])].map(x => <option key={x}>{x}</option>)}
                        </Form.Select>
                    </Form.Group>
                    <div className="d-flex flex-wrap gap-2">
                        <SubmitButton busy={busy}>Save</SubmitButton>
                        {license.revoked
                            ? <Button variant="outline-success" disabled={busy} onClick={() => save(false)}>Reinstate</Button>
                            : <Button variant="outline-danger" disabled={busy} onClick={() => setConfirmRevoke(true)}>Revoke…</Button>}
                    </div>
                </Form>
            </Card.Body>
            <ConfirmModal show={confirmRevoke} title="Revoke this licence?" confirmLabel="Revoke" busy={busy}
                          onConfirm={() => save(true)} onCancel={() => setConfirmRevoke(false)}>
                <p>The key stops activating new computers straight away.</p>
                <p className="mb-0 text-body-secondary">
                    Computers already running TSA-1 stop at their next renewal (within 30 days while online), or when their
                    lease runs out. Revoking can be undone.
                </p>
            </ConfirmModal>
        </Card>
    );
}

function Activations({license, onChanged}: { license: LicenseViewModel, onChanged(license: LicenseViewModel): void }) {
    const [freeing, setFreeing] = useState<LicenseActivationViewModel>();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<unknown>();

    const free = async () => {
        if (!freeing) {
            return;
        }
        setBusy(true);
        setError(undefined);
        try {
            await LicensesApi.freeSeat(license.id, freeing.id);
            onChanged(await LicensesApi.get(license.id));
        } catch (err) {
            setError(err);
        } finally {
            setBusy(false);
            setFreeing(undefined);
        }
    };

    return (
        <Card>
            <Card.Header>Computers ({license.activations.length} of {license.seats} seats)</Card.Header>
            <Card.Body>
                <ErrorAlert error={error} messages={{ 404: 'That computer was already deactivated.' }} onClose={() => setError(undefined)}/>
                {license.activations.length === 0 ? (
                    <p className="text-body-secondary mb-0">Not active on any computer.</p>
                ) : (
                    <Table responsive size="sm" className="mb-0">
                        <thead>
                        <tr><th>Computer</th><th>System</th><th>Version</th><th>Activated</th><th>Last seen</th><th></th></tr>
                        </thead>
                        <tbody>
                        {license.activations.map(x => (
                            <tr key={x.id}>
                                <td>{x.machineName || 'Unnamed computer'}</td>
                                <td className="small text-body-secondary">{x.os}</td>
                                <td>{x.productVersion}</td>
                                <td className="text-nowrap">{formatDateTime(x.activatedOn)}</td>
                                <td className="text-nowrap">{formatDateTime(x.lastSeen)}</td>
                                <td className="text-end">
                                    <Button size="sm" variant="outline-danger" onClick={() => setFreeing(x)}>Free seat</Button>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </Table>
                )}
            </Card.Body>
            <ConfirmModal show={!!freeing} title="Free this seat?" confirmLabel="Free seat" busy={busy} onConfirm={free} onCancel={() => setFreeing(undefined)}>
                <strong>{freeing?.machineName || 'This computer'}</strong> stops running TSA-1 at its next renewal, or when its
                lease runs out. The yearly activation count isn't refunded.
            </ConfirmModal>
        </Card>
    );
}
